import { useRef } from 'react'

import { Box, Button, Section } from '@factorialco/deprecated-design-system'
import {
  EmployeeBlocksEmployeeFragment,
  EmployeeBlocksEmployeeFragmentDoc,
  EmployeeBlocksUpdateEmployeeAddressDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import FormComponent from 'modules/core/components/Form'
import FormRow from 'modules/core/components/FormRow'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { formGetDirtyFields } from 'modules/core/lib/formGetDirtyFields'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import AddressForm from 'modules/employees/lib/forms/Personal/Address'

import AddressInput from './AddressInput'

interface Props {
  employee: EmployeeBlocksEmployeeFragment
  readonly?: boolean
}

interface WrapperProps {
  employee: EmployeeBlocksEmployeeFragment
  readonly?: boolean
  isCurrent: boolean
}

export const EmployeeAddressBlock = observer(({ employee, readonly }: Props) => {
  const [updateContactData] = useMutation(EmployeeBlocksUpdateEmployeeAddressDocument, {
    extractorFn: (data) => data.employeesMutations.updateEmployeesEmployee,
    update: (cache, result) => {
      const updatedEmployee = result.data?.employeesMutations?.updateEmployeesEmployee?.employee
      if (updatedEmployee) {
        cache.writeFragment({
          id: cache.identify(employee),
          fragment: EmployeeBlocksEmployeeFragmentDoc,
          fragmentName: 'EmployeeBlocksEmployee',
          data: {
            ...employee,
            addressLine1: updatedEmployee.addressLine1,
            addressLine2: updatedEmployee.addressLine2,
            city: updatedEmployee.city,
            country: updatedEmployee.country,
            postalCode: updatedEmployee.postalCode,
            state: updatedEmployee.state,
            contactNumber: updatedEmployee.contactNumber,
          },
        })
      }
    },
  })

  const form = useRef(
    new AddressForm({
      address_line_1: employee.addressLine1 ?? '',
      address_line_2: employee.addressLine2 ?? '',
      city: employee.city ?? '',
      country: employee.country ?? '',
      postal_code: employee.postalCode ?? '',
      state: employee.state ?? '',
    })
  )
  const addressForm = form.current

  const trackEvent = () => {
    track(trackingEvents.employee.editEmployeeInformationSubmit, {
      fields_edited: formGetDirtyFields(addressForm),
      legal_entity_country: employee.legalEntity?.country,
      layout_version: 'v1',
    })
  }

  const onSubmit = async () => {
    trackEvent()

    const result = await updateContactData({
      variables: {
        employeeId: employee.id,
        addressLine1: addressForm.get('address_line_1').value,
        addressLine2: addressForm.get('address_line_2').value,
        city: addressForm.get('city').value,
        country: addressForm.get('country').value,
        postalCode: addressForm.get('postal_code').value,
        state: addressForm.get('state').value,
      },
    })

    const handled = handleResult(result, (errors) => {
      if (errors) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('profile.employee.address.update_address_data_error'),
        })
      }
    })

    if (handled.success) {
      addressForm.cleanAll()
      app.setFlash({
        type: 'correct',
        message: i18n.t('profile.employee.address.update_address_data'),
      })
    }

    return handled
  }

  return (
    <FormComponent onSubmit={onSubmit}>
      <FormRow>
        <AddressInput mandatory form={addressForm} readonly={readonly} showCues />
      </FormRow>
      {addressForm.isDirty && (
        <FormRow footer>
          <Button submit>{i18n.t('common.submit')}</Button>
        </FormRow>
      )}
    </FormComponent>
  )
})

const EmployeeAddress = (props: WrapperProps) => {
  return (
    <Section
      illustration="sign-post"
      title={i18n.t('employee_form.address.title')}
      description={i18n.tCurrent('employee_form.address.description', props.isCurrent)}
    >
      <Box
        gap="s24"
        background="grey200"
        padding="s32"
        width="s360"
        alignSelf="flexEnd"
        borderRadius={{
          all: 'abs004',
        }}
      >
        <EmployeeAddressBlock {...props} />
      </Box>
    </Section>
  )
}

export default observer(EmployeeAddress)
